import React, { useState } from 'react';
import {
  Box,
  Typography,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const Footer: React.FC = () => {
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const currentYear = new Date().getFullYear();

  const handleTermsOpen = () => setTermsOpen(true);
  const handleTermsClose = () => setTermsOpen(false);
  const handlePrivacyOpen = () => setPrivacyOpen(true);
  const handlePrivacyClose = () => setPrivacyOpen(false);

  return (
    <>
      {/* Footer */}
      <Box
        component="footer"
        sx={{
          mt: 'auto',
          py: 3,
          px: 2,
          backgroundColor: 'rgba(15, 20, 25, 0.95)',
          borderTop: '1px solid rgba(96, 165, 250, 0.2)',
          backdropFilter: 'blur(10px)',
        }}
        className="no-print"
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 3 },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.875rem',
              textAlign: 'center',
            }}
          >
            © {currentYear} Web3 Legal Engineering. All rights reserved.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <Link
              component="button"
              onClick={handleTermsOpen}
              sx={{
                color: 'rgba(96, 165, 250, 0.8)',
                fontSize: '0.875rem',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': {
                  color: '#60a5fa',
                  textDecoration: 'underline',
                },
              }}
            >
              Terms of Use
            </Link>

            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.3)',
                fontSize: '0.75rem',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              •
            </Typography>

            <Link
              component="button"
              onClick={handlePrivacyOpen}
              sx={{
                color: 'rgba(96, 165, 250, 0.8)',
                fontSize: '0.875rem',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': {
                  color: '#60a5fa',
                  textDecoration: 'underline',
                },
              }}
            >
              Privacy Policy
            </Link>
          </Box>
        </Box>
      </Box>

      {/* Terms of Use Modal */}
      <Dialog
        open={termsOpen}
        onClose={handleTermsClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(15, 20, 25, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(96, 165, 250, 0.2)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: '#ffffff',
            borderBottom: '1px solid rgba(96, 165, 250, 0.2)',
          }}
        >
          <Typography variant="h6" component="div">
            Terms of Use
          </Typography>
          <IconButton
            onClick={handleTermsClose}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ color: 'rgba(255, 255, 255, 0.9)', py: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#60a5fa', mb: 2 }}>
            Disclaimer of Liability and Warranties
          </Typography>

          <Typography paragraph sx={{ mb: 2 }}>
            This web application is provided by Web3 Legal Engineering for the exclusive use of
            M4 Consulting LLC clients for candidacy communication and development purposes only.
          </Typography>

          <Typography variant="subtitle1" sx={{ color: '#fbbf24', mb: 1, fontWeight: 600 }}>
            No Warranties Under California Law
          </Typography>
          <Typography paragraph sx={{ mb: 2 }}>
            Under California Business and Professions Code and California Civil Code, Web3 Legal Engineering
            expressly disclaims all warranties, whether express, implied, statutory, or otherwise, including
            but not limited to warranties of merchantability, fitness for a particular purpose, and
            non-infringement. The service is provided "AS IS" and "AS AVAILABLE" without warranty of any kind.
          </Typography>

          <Typography variant="subtitle1" sx={{ color: '#fbbf24', mb: 1, fontWeight: 600 }}>
            Limitation of Liability
          </Typography>
          <Typography paragraph sx={{ mb: 2 }}>
            To the maximum extent permitted by California law, Web3 Legal Engineering shall not be liable
            for any direct, indirect, incidental, special, consequential, or punitive damages, including
            but not limited to loss of profits, data, use, or goodwill, arising out of or in connection
            with your use of this service.
          </Typography>

          <Typography variant="subtitle1" sx={{ color: '#fbbf24', mb: 1, fontWeight: 600 }}>
            California Business Code Compliance
          </Typography>
          <Typography paragraph sx={{ mb: 2 }}>
            This service operates in compliance with applicable California Business and Professions Code
            sections relating to electronic communications and data handling. Users acknowledge that this
            platform is designed solely for professional communication between M4 Consulting LLC and its clients.
          </Typography>

          <Typography variant="subtitle1" sx={{ color: '#34d399', mb: 1, fontWeight: 600 }}>
            Authorized Use Only
          </Typography>
          <Typography paragraph>
            Access to this system is restricted to authorized M4 Consulting LLC candidates and clients only.
            Unauthorized access, use, or distribution of information contained herein is strictly prohibited
            and may result in civil and criminal penalties under applicable state and federal laws.
          </Typography>

          <Divider sx={{ my: 3, borderColor: 'rgba(96, 165, 250, 0.2)' }} />

          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Last updated: {new Date().toLocaleDateString()}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid rgba(96, 165, 250, 0.2)', p: 2 }}>
          <Button onClick={handleTermsClose} variant="outlined" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Privacy Policy Modal */}
      <Dialog
        open={privacyOpen}
        onClose={handlePrivacyClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(15, 20, 25, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(96, 165, 250, 0.2)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: '#ffffff',
            borderBottom: '1px solid rgba(96, 165, 250, 0.2)',
          }}
        >
          <Typography variant="h6" component="div">
            Privacy Policy
          </Typography>
          <IconButton
            onClick={handlePrivacyClose}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ color: 'rgba(255, 255, 255, 0.9)', py: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#60a5fa', mb: 2 }}>
            Privacy & Data Protection
          </Typography>

          <Typography paragraph sx={{ mb: 2 }}>
            Web3 Legal Engineering is committed to protecting your privacy and ensuring compliance with
            all applicable privacy regulations, including the California Consumer Privacy Act (CCPA)
            and the General Data Protection Regulation (GDPR).
          </Typography>

          <Typography variant="subtitle1" sx={{ color: '#34d399', mb: 1, fontWeight: 600 }}>
            CCPA Compliance
          </Typography>
          <Typography paragraph sx={{ mb: 2 }}>
            Under the California Consumer Privacy Act (CCPA), we affirm that:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <Typography component="li" sx={{ mb: 0.5 }}>
              We do not sell, share, or transfer your personal information to third parties
            </Typography>
            <Typography component="li" sx={{ mb: 0.5 }}>
              You have the right to know what personal information is collected about you
            </Typography>
            <Typography component="li" sx={{ mb: 0.5 }}>
              You have the right to request deletion of your personal information
            </Typography>
            <Typography component="li" sx={{ mb: 0.5 }}>
              You have the right to opt-out of the sale of personal information (not applicable as we don't sell data)
            </Typography>
            <Typography component="li" sx={{ mb: 0.5 }}>
              You have the right to non-discriminatory treatment for exercising your privacy rights
            </Typography>
          </Box>

          <Typography variant="subtitle1" sx={{ color: '#34d399', mb: 1, fontWeight: 600 }}>
            GDPR Compliance
          </Typography>
          <Typography paragraph sx={{ mb: 2 }}>
            For users subject to the General Data Protection Regulation (GDPR), we ensure:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <Typography component="li" sx={{ mb: 0.5 }}>
              Lawful basis for processing personal data (legitimate business interest for client communication)
            </Typography>
            <Typography component="li" sx={{ mb: 0.5 }}>
              Data minimization - we only collect data necessary for candidacy communication
            </Typography>
            <Typography component="li" sx={{ mb: 0.5 }}>
              Right to access, rectify, erase, restrict processing, and data portability
            </Typography>
            <Typography component="li" sx={{ mb: 0.5 }}>
              Right to object to processing and withdraw consent
            </Typography>
            <Typography component="li" sx={{ mb: 0.5 }}>
              Data security and breach notification procedures
            </Typography>
          </Box>

          <Typography variant="subtitle1" sx={{ color: '#fbbf24', mb: 1, fontWeight: 600 }}>
            Data Collection & Use
          </Typography>
          <Typography paragraph sx={{ mb: 2 }}>
            We collect only the information necessary to facilitate candidacy communication between
            M4 Consulting LLC and its clients, including:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <Typography component="li" sx={{ mb: 0.5 }}>
              Contact information (name, email) for authentication
            </Typography>
            <Typography component="li" sx={{ mb: 0.5 }}>
              Interview progress and timeline data
            </Typography>
            <Typography component="li" sx={{ mb: 0.5 }}>
              Questions and responses for candidate communication
            </Typography>
            <Typography component="li" sx={{ mb: 0.5 }}>
              Technical data necessary for system operation (access logs, session data)
            </Typography>
          </Box>

          <Typography variant="subtitle1" sx={{ color: '#f87171', mb: 1, fontWeight: 600 }}>
            No Data Sharing or Sale
          </Typography>
          <Typography paragraph sx={{ mb: 2 }}>
            We explicitly state that no personal information, candidate data, or any other information
            collected through this system is shared, sold, transferred, or disclosed to any third parties,
            except as required by law or with explicit user consent.
          </Typography>

          <Typography variant="subtitle1" sx={{ color: '#60a5fa', mb: 1, fontWeight: 600 }}>
            Data Security
          </Typography>
          <Typography paragraph sx={{ mb: 2 }}>
            We implement industry-standard security measures including encryption, access controls,
            and secure authentication to protect your personal information from unauthorized access,
            use, or disclosure.
          </Typography>

          <Typography variant="subtitle1" sx={{ color: '#a78bfa', mb: 1, fontWeight: 600 }}>
            Your Rights
          </Typography>
          <Typography paragraph>
            To exercise your privacy rights under CCPA or GDPR, or for any privacy-related questions,
            please contact your M4 Consulting representative who will coordinate with Web3 Legal Engineering
            to address your request within the required timeframes.
          </Typography>

          <Divider sx={{ my: 3, borderColor: 'rgba(96, 165, 250, 0.2)' }} />

          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Last updated: {new Date().toLocaleDateString()}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid rgba(96, 165, 250, 0.2)', p: 2 }}>
          <Button onClick={handlePrivacyClose} variant="outlined" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Footer;